import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";

import type { IApiResponse } from "@models/index";
import { RequestService } from "@services/index";

/**
 * The admin's own judgement of a vendor, 0-100.
 *
 * It is the one part of a product's score a person sets: the other five are
 * counted from the catalogue and the order book. Fifty is where every vendor
 * starts, existing and new, and means "not yet judged" rather than "judged
 * average" - a default of zero would have read as a verdict nobody passed and
 * dragged every one of their products down until someone rated them.
 */
@Component({
  selector: "fh-vendor-performance",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
    <div class="card">
      <div class="card-header card-header-danger">
        <h4 class="card-title">Vendor Performance</h4>
        <p class="card-category">
          Carries 15% of the score of every product this vendor sells.
        </p>
      </div>
      <div class="card-body">
        <mat-form-field appearance="outline" style="max-width: 220px">
          <mat-label>Vendor score</mat-label>
          <input matInput type="number" min="0" max="100" step="1" [formControl]="scoreControl" />
          <mat-hint>0 to 100. Everyone starts at 50.</mat-hint>
          @if (scoreControl.hasError('required')) {
            <mat-error>A score is required</mat-error>
          }
          @if (scoreControl.hasError('min') || scoreControl.hasError('max')) {
            <mat-error>Must be between 0 and 100</mat-error>
          }
          @if (scoreControl.hasError('pattern')) {
            <mat-error>Whole numbers only</mat-error>
          }
        </mat-form-field>

        <div class="mt-3">
          <button
            mat-raised-button
            color="primary"
            (click)="save()"
            [disabled]="scoreControl.invalid || saving()"
          >
            {{ saving() ? 'Saving…' : 'Save' }}
          </button>
        </div>

        @if (success()) {
          <div class="alert alert-success mt-3">{{ success() }}</div>
        }
        @if (error()) {
          <div class="alert alert-danger mt-3">{{ error() }}</div>
        }
      </div>
    </div>
  `,
})
export class VendorPerformanceComponent implements OnInit {
  @Input() vendorId!: string;

  /** Whole numbers only: the score is stored as a smallint and read as one. */
  readonly scoreControl = new FormControl<number | null>(50, [
    Validators.required,
    Validators.min(0),
    Validators.max(100),
    Validators.pattern(/^\d+$/),
  ]);

  saving = signal(false);
  success = signal("");
  error = signal("");

  constructor(private requestService: RequestService) {}

  ngOnInit(): void {
    if (!this.vendorId) return;
    this.requestService.get<IApiResponse>(`/api/v2/vendor/${this.vendorId}/performance`).subscribe({
      next: (res) => {
        if (res.status) {
          this.scoreControl.setValue(Number((res.data as { vendorScore: number }).vendorScore));
        }
      },
      // The field keeps its default of 50 rather than showing an empty box:
      // an admin who saves it then has set what was already true.
      error: () => this.error.set("Could not load this vendor's score"),
    });
  }

  save(): void {
    if (this.scoreControl.invalid || this.saving()) return;
    this.saving.set(true);
    this.success.set("");
    this.error.set("");

    this.requestService
      .put<IApiResponse>(`/api/v2/vendor/${this.vendorId}/performance`, {
        vendorScore: Number(this.scoreControl.value),
      })
      .subscribe({
        next: (res) => {
          this.saving.set(false);
          if (res.status) {
            this.success.set("Vendor score saved. Their products are scored with it from now on.");
          } else {
            this.error.set(res.message || "Failed to save");
          }
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set(err?.error?.message || "Failed to save");
        },
      });
  }
}
